import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";
import { agentEvidence, eventEvidence } from "./aiDevelopmentViewModel";
import AiWorkflowProgress, { isWorkflowProblem } from "./AiWorkflowProgress";
import useIncrementalAiEvents from "./useIncrementalAiEvents";
import AiStoryForm, { storyPayload, storyToForm } from "./AiStoryForm";
import RichTextEditor from "./RichTextEditor";
import RichContent from "./RichContent";
import ImageLightbox from "./ImageLightbox";
import ImageAnalysisPanel from "./ImageAnalysisPanel";
import AiTaigaStorySyncPanel from "./AiTaigaStorySyncPanel";
import AiJiraStorySyncPanel from "./AiJiraStorySyncPanel";
import AiAzureDevOpsStorySyncPanel from "./AiAzureDevOpsStorySyncPanel";
import AiRelationshipsPanel from "./AiRelationshipsPanel";
import { useAuth } from "../../contexts/AuthContext";
import "./AiDevelopmentStudio.css";

const inlineImageIds = (html) => {
  if (!html) return [];
  const matches = html.matchAll(/data-attachment-id="([^"]+)"/g);
  return Array.from(matches, (m) => m[1]);
};

export default function AiDevelopmentStoryPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canRequestAgentAction = hasPermission("ai.development.request");
  const canApproveAgentAction = hasPermission("ai.development.approve");
  const [data, setData] = useState({});
  const [projects, setProjects] = useState([]);
  const [editForm, setEditForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [diff, setDiff] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);
  const [storyComments, setStoryComments] = useState([]);
  const [storyComment, setStoryComment] = useState("");
  useIncrementalAiEvents(id, data.events || [], setData);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const story = await aiDevelopmentService.story(id);
      const optional = async (fn) => {
        try {
          return await fn();
        } catch {
          return null;
        }
      };
      const [plan, findings, runs, events, projectRows, attachments, comments, productOwnerReviews] =
        await Promise.all([
          optional(() => aiDevelopmentService.plan(id)),
          optional(() => aiDevelopmentService.findings(id)),
          optional(() => aiDevelopmentService.agentRuns(id)),
          optional(() => aiDevelopmentService.events(id)),
          aiDevelopmentService.projects(),
          optional(() => aiDevelopmentService.storyAttachments(id)),
          optional(() => aiDevelopmentService.storyComments(id)),
          optional(() => aiDevelopmentService.productOwnerReviews(id)),
        ]);
      setProjects(projectRows || []);
      setStoryComments(comments || []);
      setData({
        story,
        plan,
        findings: findings || [],
        runs: runs || [],
        events: events || [],
        attachments: attachments || [],
        productOwnerReviews: productOwnerReviews || [],
      });
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);
  useEffect(() => {
    load();
  }, [load]);
  const action = async (fn) => {
    setLoading(true);
    setError("");
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
      setLoading(false);
      return false;
    }
  };
  const loadDiff = async () => {
    setDiffLoading(true);
    setError("");
    try {
      setDiff((await aiDevelopmentService.toolingDiff(id)) || []);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setDiffLoading(false);
    }
  };
  const addStoryComment = async () => {
    await aiDevelopmentService.addStoryComment(id, storyComment);
    setStoryComment("");
    setStoryComments(await aiDevelopmentService.storyComments(id));
  };
  const saveDraft = async (e) => {
    e.preventDefault();
    if (
      await action(() =>
        aiDevelopmentService.updateStory(id, storyPayload(editForm)),
      )
    )
      setEditForm(null);
  };
  if (loading && !data.story)
    return <div className="ai-studio-page">{t("common.loading")}</div>;
  const s = data.story;
  if (!s) return <div className="ai-studio-page ai-studio-alert">{error}</div>;
  return (
    <div className="ai-studio-page">
      <header className="ai-studio-header">
        <div>
          <Link to="/ai-development/stories">← {t("aiDevelopment.back")}</Link>
          <h1>{s.title}</h1>
          <p>
            {s.externalReference || s.id} · {s.projectName || s.projectId}
            {s.taigaUrl && (
              <>
                {" · "}
                <a href={s.taigaUrl} target="_blank" rel="noreferrer">
                  {t("aiDevelopment.viewInTaiga")}
                </a>
              </>
            )}
            {s.jiraUrl && (
              <>
                {" · "}
                <a href={s.jiraUrl} target="_blank" rel="noreferrer">
                  {t("aiDevelopment.viewInJira")}
                </a>
              </>
            )}
            {s.azureDevOpsUrl && (
              <>
                {" · "}
                <a href={s.azureDevOpsUrl} target="_blank" rel="noreferrer">
                  {t("aiDevelopment.viewInAzureDevOps")}
                </a>
              </>
            )}
          </p>
        </div>
        <span className="ai-status ai-status-large">{s.status}</span>
      </header>
      {error && <div className="ai-studio-alert">{error}</div>}
      <AiWorkflowProgress status={s.status} t={t} />
      {isWorkflowProblem(s.status) && (
        <div className="ai-studio-alert" role="alert">
          {t("aiDevelopment.workflowNeedsAttention")}
        </div>
      )}
      {s.status === "FAILED" && (
        <div className="ai-studio-alert" role="alert">
          {t("aiDevelopment.storyFailedTerminal")}
        </div>
      )}
      {s.status === "WAITING_FOR_APPROVAL" && (
        <section className="ai-studio-panel ai-approval-panel">
          <label>
            {t("aiDevelopment.approvalComment")}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength="2000"
            />
          </label>
        </section>
      )}
      <div className="ai-actions">
        {s.status === "DRAFT" && (
          <>
            <button
              className="btn btn-default"
              onClick={() => setEditForm(storyToForm(s))}
            >
              {t("common.edit")}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => action(() => aiDevelopmentService.submit(id))}
            >
              {t("aiDevelopment.submit")}
            </button>
          </>
        )}
        {s.status === "READY_FOR_ANALYSIS" && (
          <button
            className="btn btn-primary"
            onClick={() =>
              action(() => aiDevelopmentService.requestAnalysis(id))
            }
          >
            {t("aiDevelopment.analyse")}
          </button>
        )}
        {s.status === "WAITING_FOR_APPROVAL" && (
          <>
            <button
              className="btn btn-primary"
              onClick={() =>
                action(() => aiDevelopmentService.approvePlan(id, comment))
              }
            >
              {t("aiDevelopment.approve")}
            </button>
            <button
              className="btn btn-default"
              onClick={() =>
                action(() => aiDevelopmentService.rejectPlan(id, comment))
              }
            >
              {t("aiDevelopment.reject")}
            </button>
          </>
        )}
        {["DEVELOPMENT_BLOCKED", "QA_FAILED"].includes(s.status) && (
          <button
            className="btn btn-primary"
            onClick={() => action(() => aiDevelopmentService.resume(id))}
          >
            {t("aiDevelopment.resume")}
          </button>
        )}
        <button className="btn btn-default" onClick={load}>
          {t("aiDevelopment.refresh")}
        </button>
        <button
          className="btn btn-default"
          onClick={loadDiff}
          disabled={diffLoading}
        >
          {t("aiDevelopment.viewDiff")}
        </button>
      </div>
      {editForm && (
        <AiStoryForm
          form={editForm}
          setForm={setEditForm}
          projects={projects}
          loading={loading}
          onSubmit={saveDraft}
          onCancel={() => setEditForm(null)}
          title={t("aiDevelopment.editStory")}
          submitLabel={t("common.save")}
          t={t}
          storyId={id}
        />
      )}
      <section className="ai-studio-panel">
        <h2>{t("aiDevelopment.overview")}</h2>
        <p>
          <strong>{t("aiDevelopment.project")}:</strong>{" "}
          {s.projectName || s.projectId}
        </p>
        <RichContent html={s.description} attachments={data.attachments} />
        {s.businessContext && (
          <RichContent html={s.businessContext} attachments={data.attachments} />
        )}
        <ul>
          {s.acceptanceCriteria?.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
        <ImageUpload
          label={t("aiDevelopment.attachImages")}
          onFile={(file) =>
            action(() => aiDevelopmentService.attachStoryImage(id, file))
          }
        />
        <AttachmentList
          attachments={data.attachments}
          excludeIds={[
            ...inlineImageIds(s.description),
            ...inlineImageIds(s.businessContext),
          ]}
        />
      </section>
      <section className="ai-studio-panel">
        <AiTaigaStorySyncPanel storyId={id} originSystem={s.originSystem} t={t} />
        <AiJiraStorySyncPanel storyId={id} originSystem={s.originSystem} t={t} />
        <AiAzureDevOpsStorySyncPanel storyId={id} originSystem={s.originSystem} t={t} />
        <div className="ai-comments">
          <h4>{t("aiDevelopment.comments")}</h4>
          {storyComments.map((v) => (
            <div className="ai-comment" key={v.id}>
              {v.externalAuthor && (
                <small>
                  {t(
                    v.provider === "JIRA"
                      ? "aiDevelopment.jiraSync.originJira"
                      : v.provider === "AZURE_DEVOPS"
                      ? "aiDevelopment.azureDevOpsSync.originAzureDevOps"
                      : "aiDevelopment.taigaSync.originTaiga",
                  )}
                  : {v.externalAuthor}
                </small>
              )}
              <RichContent html={v.bodyHtml} attachments={v.attachments} />
              <AttachmentList attachments={v.attachments} />
            </div>
          ))}
          <RichTextEditor
            value={storyComment}
            onChange={setStoryComment}
            label={t("aiDevelopment.newComment")}
            onUploadImage={(file) => aiDevelopmentService.attachStoryImage(id, file)}
          />
          <button
            className="btn btn-primary"
            disabled={!storyComment}
            onClick={addStoryComment}
          >
            {t("aiDevelopment.addComment")}
          </button>
        </div>
      </section>
      <section className="ai-studio-panel">
        <AiRelationshipsPanel itemType="STORY" itemId={id} t={t} />
      </section>
      <section className="ai-studio-panel">
        <h2>{t("aiDevelopment.tasks")}</h2>
        <div className="ai-card-grid">
          {data.plan?.tasks?.length ? (
            data.plan.tasks.map((task) => (
              <TaskCard key={task.id} task={task} t={t} onChanged={load} />
            ))
          ) : (
            <p>{t("common.noData")}</p>
          )}
        </div>
      </section>
      {(diffLoading || diff.length > 0) && (
        <section className="ai-studio-panel">
          <h2>{t("aiDevelopment.diff")}</h2>
          {diffLoading ? (
            <p>{t("common.loading")}</p>
          ) : diff.length ? (
            diff.map((r) => (
              <div key={r.repositoryId} className="ai-diff-repository">
                <h3>{r.repositoryName || r.repositoryId}</h3>
                <pre className="ai-diff">{r.diff || t("aiDevelopment.noDiff")}</pre>
              </div>
            ))
          ) : (
            <p>{t("aiDevelopment.noDiff")}</p>
          )}
        </section>
      )}
      <section className="ai-studio-grid">
        <div className="ai-studio-panel">
          <h2>{t("aiDevelopment.findings")}</h2>
          {data.findings.length ? (
            data.findings.map((v) => (
              <FindingCard key={v.id} finding={v} t={t} />
            ))
          ) : (
            <p>{t("common.noData")}</p>
          )}
        </div>
        <div className="ai-studio-panel">
          <h2>{t("aiDevelopment.agentRuns")}</h2>
          {data.runs.length ? (
            data.runs.map((v) => <AgentRunCard key={v.id} run={v} t={t} />)
          ) : (
            <p>{t("common.noData")}</p>
          )}
        </div>
      </section>
      <section className="ai-studio-panel">
        <h2>{t("aiDevelopment.productOwner.title")}</h2>
        {canRequestAgentAction ? (
          <button
            type="button"
            className="btn btn-default"
            onClick={() => action(() => aiDevelopmentService.requestProductOwnerReview(id))}
          >
            {t("aiDevelopment.productOwner.requestReview")}
          </button>
        ) : (
          <p className="ai-permission-hint">{t("aiDevelopment.productOwner.noPermission")}</p>
        )}
        {(data.productOwnerReviews || []).length ? (
          data.productOwnerReviews.map((review) => (
            <ProductOwnerReviewCard
              key={review.id}
              review={review}
              t={t}
              canDecide={canApproveAgentAction}
              onDecide={(accepted) =>
                action(() =>
                  aiDevelopmentService.decideProductOwnerReview(review.id, {
                    accepted,
                    note: "",
                    version: review.version,
                  }),
                )
              }
            />
          ))
        ) : (
          <p>{t("common.noData")}</p>
        )}
      </section>
      <section className="ai-studio-panel">
        <h2>{t("aiDevelopment.timeline")}</h2>
        {data.events.length ? (
          data.events.map((v) => <EventCard key={v.id} event={v} t={t} />)
        ) : (
          <p>{t("common.noData")}</p>
        )}
      </section>
    </div>
  );
}

function TaskCard({ task, t, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(task);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [comment, setComment] = useState("");
  const [providers, setProviders] = useState([]);
  const [showRelationships, setShowRelationships] = useState(false);
  const refresh = useCallback(async () => {
    setComments(await aiDevelopmentService.taskComments(task.id));
    setAttachments(await aiDevelopmentService.taskAttachments(task.id));
  }, [task.id]);
  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);
  useEffect(() => {
    aiDevelopmentService
      .executionProviders()
      .then((list) => setProviders((list || []).filter((p) => p.enabled)))
      .catch(() => setProviders([]));
  }, []);
  const editable = ["DRAFT", "PLANNED", "REWORK_PLANNED"].includes(task.status);
  const save = async () => {
    await aiDevelopmentService.updateTask(task.id, {
      title: form.title,
      description: form.description,
      acceptanceCriteria: form.acceptanceCriteria,
      implementationStrategy: form.implementationStrategy,
      testStrategy: form.testStrategy,
      providerOverride: form.providerOverride || null,
      version: task.version,
    });
    setEditing(false);
    // Images inserted while editing are uploaded immediately (before save), so the saved
    // description can reference attachments this component's `attachments` state doesn't know
    // about yet - without this, the analysis panel/attachment list for a just-added image stays
    // stale until something else (e.g. task.id changing) happens to trigger a refetch.
    await refresh();
    onChanged();
  };
  const addComment = async () => {
    await aiDevelopmentService.addTaskComment(task.id, comment);
    setComment("");
    await refresh();
  };
  return (
    <article className="ai-card">
      <div>
        <strong>
          {task.sequence}. {task.title}
        </strong>
        <span className="ai-status">{task.status}</span>
      </div>
      {editing ? (
        <div className="ai-task-editor">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <RichTextEditor
            value={form.description}
            onChange={(description) => setForm({ ...form, description })}
            label={t("aiDevelopment.description")}
            onUploadImage={(file) =>
              aiDevelopmentService.attachTaskImage(task.id, file)
            }
          />
          <ListEditor
            label={t("aiDevelopment.acceptance")}
            value={form.acceptanceCriteria}
            onChange={(acceptanceCriteria) =>
              setForm({ ...form, acceptanceCriteria })
            }
          />
          <ListEditor
            label={t("aiDevelopment.implementationStrategy")}
            value={form.implementationStrategy}
            onChange={(implementationStrategy) =>
              setForm({ ...form, implementationStrategy })
            }
          />
          <ListEditor
            label={t("aiDevelopment.testStrategy")}
            value={form.testStrategy}
            onChange={(testStrategy) => setForm({ ...form, testStrategy })}
          />
          <label>
            {t("aiDevelopment.taskExecutionOverride")}
            <select
              value={form.providerOverride || ""}
              onChange={(e) =>
                setForm({ ...form, providerOverride: e.target.value })
              }
            >
              <option value="">{t("aiDevelopment.inheritFromStory")}</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-primary" onClick={save}>
            {t("common.save")}
          </button>
          <button className="btn btn-default" onClick={() => setEditing(false)}>
            {t("common.cancel")}
          </button>
        </div>
      ) : (
        <RichContent html={task.description} attachments={attachments} />
      )}
      <small>
        {task.repository}
        {task.module ? ` · ${task.module}` : ""} · {task.assignedAgent}
      </small>
      {editable && !editing && (
        <button className="btn btn-default" onClick={() => setEditing(true)}>
          {t("common.edit")}
        </button>
      )}
      <details>
        <summary>{t("aiDevelopment.taskDetails")}</summary>
        <EvidenceList
          title={t("aiDevelopment.acceptance")}
          values={task.acceptanceCriteria}
        />
        <EvidenceList
          title={t("aiDevelopment.implementationStrategy")}
          values={task.implementationStrategy}
        />
        <EvidenceList
          title={t("aiDevelopment.testStrategy")}
          values={task.testStrategy}
        />
        <EvidenceList
          title={t("aiDevelopment.dependencies")}
          values={task.dependencies}
        />
      </details>
      <ImageUpload
        label={t("aiDevelopment.attachImages")}
        onFile={async (file) => {
          await aiDevelopmentService.attachTaskImage(task.id, file);
          await refresh();
        }}
      />
      <AttachmentList attachments={attachments} />
      <div className="ai-comments">
        <h4>{t("aiDevelopment.comments")}</h4>
        {comments.map((v) => (
          <div className="ai-comment" key={v.id}>
            <RichContent html={v.bodyHtml} attachments={v.attachments} />
            <AttachmentList attachments={v.attachments} />
          </div>
        ))}
        <RichTextEditor
          value={comment}
          onChange={setComment}
          label={t("aiDevelopment.newComment")}
          onUploadImage={(file) =>
            aiDevelopmentService.attachTaskImage(task.id, file)
          }
        />
        <button
          className="btn btn-primary"
          disabled={!comment}
          onClick={addComment}
        >
          {t("aiDevelopment.addComment")}
        </button>
      </div>
      <button
        type="button"
        className="btn btn-default"
        onClick={() => setShowRelationships((v) => !v)}
      >
        {t("aiDevelopment.relationships.title")}
      </button>
      {showRelationships && (
        <AiRelationshipsPanel itemType="TASK" itemId={task.id} t={t} />
      )}
    </article>
  );
}
function ListEditor({ label, value, onChange }) {
  return (
    <label>
      {label}
      <textarea
        value={(value || []).join("\n")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split("\n")
              .map((v) => v.trim())
              .filter(Boolean),
          )
        }
      />
    </label>
  );
}
function ImageUpload({ label, onFile }) {
  return (
    <label className="btn btn-default ai-image-upload">
      {label}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = "";
        }}
      />
    </label>
  );
}
function AttachmentList({ attachments = [], excludeIds = [] }) {
  const { t } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const visible = attachments.filter((v) => !excludeIds.includes(v.id));
  const ids = visible.map((v) => v.id);
  const attachmentById = Object.fromEntries(visible.map((v) => [v.id, v]));
  if (!visible.length) return null;
  return (
    <div className="ai-attachments">
      {visible.map((v, i) => (
        <AttachmentPreview
          key={v.id}
          attachment={v}
          onOpen={() => setLightboxIndex(i)}
          onViewAnalysis={() => setAnalysisId(v.id)}
          t={t}
        />
      ))}
      {analysisId && (
        <ImageAnalysisPanel
          attachment={attachmentById[analysisId]}
          onClose={() => setAnalysisId(null)}
        />
      )}
      {lightboxIndex !== null && (
        <ImageLightbox
          attachmentIds={ids}
          index={lightboxIndex}
          attachmentById={attachmentById}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}
function AttachmentPreview({ attachment, onOpen, onViewAnalysis, t }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let current = "";
    aiDevelopmentService.attachmentBlob(attachment.id).then((blob) => {
      current = URL.createObjectURL(blob);
      setUrl(current);
    });
    return () => {
      if (current) URL.revokeObjectURL(current);
    };
  }, [attachment.id]);
  return (
    <figure className="ai-inline-image-wrap">
      {url && (
        <img
          className="ai-inline-image"
          src={url}
          alt={attachment.fileName}
          tabIndex={0}
          role="button"
          aria-label={t("aiDevelopment.openImage")}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          }}
        />
      )}
      <button
        type="button"
        className="ai-image-analysis-toggle"
        aria-label={t("aiDevelopment.viewAiAnalysis")}
        onClick={onViewAnalysis}
      >
        ⓘ
      </button>
      <figcaption>{attachment.fileName}</figcaption>
    </figure>
  );
}
function ProductOwnerReviewCard({ review, t, canDecide, onDecide }) {
  let issues = [];
  let criteria = [];
  try {
    issues = JSON.parse(review.definitionOfReadyIssues || "[]");
  } catch {
    issues = [];
  }
  try {
    criteria = JSON.parse(review.proposedAcceptanceCriteria || "[]");
  } catch {
    criteria = [];
  }
  return (
    <article className="ai-event">
      <strong>
        {t(`aiDevelopment.productOwner.status.${review.status}`)}
        {review.proposedStoryPoints != null &&
          ` · ${t("aiDevelopment.productOwner.storyPoints")}: ${review.proposedStoryPoints}`}
        {review.proposedBusinessValue != null &&
          ` · ${t("aiDevelopment.productOwner.businessValue")}: ${review.proposedBusinessValue}`}
      </strong>
      {issues.length > 0 && (
        <EvidenceList title={t("aiDevelopment.productOwner.dorIssues")} values={issues} />
      )}
      {criteria.length > 0 && (
        <EvidenceList
          title={t("aiDevelopment.productOwner.proposedCriteria")}
          values={criteria}
        />
      )}
      {review.rationale && <p>{review.rationale}</p>}
      {review.status === "PROPOSED" && canDecide && (
        <div className="ai-actions">
          <button type="button" className="btn btn-primary" onClick={() => onDecide(true)}>
            {t("aiDevelopment.productOwner.accept")}
          </button>
          <button type="button" className="btn btn-default" onClick={() => onDecide(false)}>
            {t("aiDevelopment.productOwner.reject")}
          </button>
        </div>
      )}
    </article>
  );
}
function FindingCard({ finding: v, t }) {
  return (
    <article
      className={`ai-event ai-severity-${String(v.severity).toLowerCase()}`}
    >
      <strong>
        {v.severity}: {v.title}
      </strong>
      <p>{v.description}</p>
      <dl className="ai-evidence-grid">
        <dt>{t("aiDevelopment.expected")}</dt>
        <dd>{v.expectedBehavior}</dd>
        <dt>{t("aiDevelopment.actual")}</dt>
        <dd>{v.actualBehavior}</dd>
        <dt>{t("aiDevelopment.evidence")}</dt>
        <dd>{v.evidence}</dd>
      </dl>
      {v.file && (
        <small>
          {v.file}
          {v.line ? `:${v.line}` : ""} · {v.status}
        </small>
      )}
    </article>
  );
}
function AgentRunCard({ run, t }) {
  const evidence = agentEvidence(run);
  return (
    <article className="ai-event">
      <strong>
        {run.agentType} · {run.status}
      </strong>
      <p>{run.error || evidence.summary}</p>
      {evidence.filesChanged.length > 0 && (
        <EvidenceList
          title={t("aiDevelopment.filesChanged")}
          values={evidence.filesChanged}
        />
      )}{" "}
      {evidence.tests.length > 0 && (
        <div>
          <b>{t("aiDevelopment.tests")}</b>
          {evidence.tests.map((test, i) => (
            <span
              className={`ai-test ai-test-${String(test.status).toLowerCase()}`}
              key={i}
            >
              {test.name}: {test.status}
            </span>
          ))}
        </div>
      )}
      <small>
        {run.model} · {run.promptTemplateVersion}
      </small>
      <details>
        <summary>{t("aiDevelopment.technicalEvidence")}</summary>
        <pre className="ai-json">{JSON.stringify(evidence.raw, null, 2)}</pre>
      </details>
    </article>
  );
}
function EventCard({ event: v, t }) {
  const evidence = eventEvidence(v);
  return (
    <article className="ai-event">
      <time>{new Date(v.createdAt).toLocaleString()}</time>
      <strong>{v.eventType}</strong>
      <p>{v.message}</p>
      {v.eventType === "AI_CHANGE_PUBLISHED" && (
        <div className="ai-publication">
          <span>
            {t("aiDevelopment.commit")}: <code>{evidence.commitSha}</code>
          </span>
          {evidence.mergeRequestUrl && (
            <a href={evidence.mergeRequestUrl} target="_blank" rel="noreferrer">
              {t("aiDevelopment.openMergeRequest")}
            </a>
          )}
        </div>
      )}{" "}
      {Object.keys(evidence).length > 0 && (
        <details>
          <summary>{t("aiDevelopment.technicalEvidence")}</summary>
          <pre className="ai-json">{JSON.stringify(evidence, null, 2)}</pre>
        </details>
      )}
    </article>
  );
}
function EvidenceList({ title, values }) {
  if (!values?.length) return null;
  return (
    <div className="ai-evidence-list">
      <b>{title}</b>
      <ul>
        {values.map((value, i) => (
          <li key={i}>
            {typeof value === "string" ? value : JSON.stringify(value)}
          </li>
        ))}
      </ul>
    </div>
  );
}
